// FILE: src/pages/Medicaid/MedicaidPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { medicaidAPI, clientsAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Medicaid.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function MedicaidPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','Billing','CaseManager');

  const [records,   setRecords]   = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [clientFilt,setClientFilt]= useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [mRes, cRes] = await Promise.all([medicaidAPI.getAll(), clientsAPI.getAll()]);
      setRecords(mRes.data.data  || []);
      setFiltered(mRes.data.data  || []);
      setClients(cRes.data.data   || []);
    } catch { toast.error('Failed to load Medicaid records.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...records];
    if (clientFilt) data = data.filter(r => String(r.ClientID) === clientFilt);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        (r.ClientName||'').toLowerCase().includes(q) ||
        (r.AuthorizationNumber||'').toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, clientFilt, records]);

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await medicaidAPI.delete(deleting.ID);
      toast.success('Authorization deleted.');
      setDeleting(null); load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const totalAuth = records.reduce((s,r)=>s+parseFloat(r.AuthorizedHours||0),0);
  const totalUsed = records.reduce((s,r)=>s+parseFloat(r.UsedHours||0),0);
  const overLimit = records.filter(r=>parseFloat(r.UsedHours||0)>parseFloat(r.AuthorizedHours||0)).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Medicaid Authorizations</h1>
          <p>{records.length} authorizations</p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search client, auth number…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:220}}/>
          </div>
          <select className="form-control" style={{width:190}} value={clientFilt}
            onChange={e=>setClientFilt(e.target.value)}>
            <option value="">All Clients</option>
            {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
          </select>
          {canEdit && (
            <button className="btn btn-primary" onClick={()=>{ setEditing(null); setShowModal(true); }}>➕ Add Authorization</button>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Authorizations',  value:records.length,           icon:'📋'},
          {label:'Total Auth Hours',value:`${totalAuth.toFixed(0)}h`, icon:'✅'},
          {label:'Hours Used',      value:`${totalUsed.toFixed(0)}h`, icon:'⏰'},
          {label:'Over Limit',      value:overLimit,                 icon:'⚠️'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading Medicaid records…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏥</div>
            <div className="empty-state-text">{search||clientFilt ? 'No records match.' : 'No authorizations yet.'}</div>
            {!search && !clientFilt && canEdit && (
              <button className="btn btn-primary" style={{marginTop:14}} onClick={()=>{ setEditing(null); setShowModal(true); }}>➕ Add First Authorization</button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr><th>Client</th><th>Period</th><th>Auth #</th><th>Auth Hrs</th><th>Used Hrs</th><th>Utilization</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(r=>{
                  const auth = parseFloat(r.AuthorizedHours||0);
                  const used = parseFloat(r.UsedHours||0);
                  const pct  = auth>0 ? Math.min(100,Math.round((used/auth)*100)) : 0;
                  const over = used > auth;
                  return (
                    <tr key={r.ID}>
                      <td className="bold">{r.ClientName||'—'}</td>
                      <td style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{MONTHS[(r.AuthMonth||1)-1]} {r.AuthYear}</td>
                      <td style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{r.AuthorizationNumber||'—'}</td>
                      <td style={{fontWeight:600}}>{auth}h</td>
                      <td style={{fontWeight:600,color:over?'var(--danger)':'var(--gray-800)'}}>{used.toFixed(1)}h</td>
                      <td style={{minWidth:160}}>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{flex:1,height:8,background:'var(--gray-100)',borderRadius:99,overflow:'hidden'}}>
                            <div style={{
                              width:`${pct}%`,height:'100%',borderRadius:99,
                              background: pct>=100?'var(--danger)':pct>=80?'var(--warning)':'var(--success)',
                              transition:'width 0.4s'
                            }}/>
                          </div>
                          <span style={{fontSize:'0.78rem',fontWeight:600,minWidth:36,
                            color:pct>=100?'var(--danger)':pct>=80?'var(--warning)':'var(--success)'}}>
                            {pct}%
                          </span>
                        </div>
                        {over && <div style={{fontSize:'0.7rem',color:'var(--danger)',fontWeight:600,marginTop:2}}>⚠️ Over limit by {(used-auth).toFixed(1)}h</div>}
                      </td>
                      <td><span className={`badge ${r.IsActive?'badge-success':'badge-gray'}`}>{r.IsActive?'Active':'Inactive'}</span></td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          {canEdit && (
                            <>
                              <button className="btn btn-sm btn-secondary" onClick={()=>{ setEditing(r); setShowModal(true); }}>✏️</button>
                              <button className="btn btn-sm btn-danger"    onClick={()=>setDeleting(r)}>🗑️</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <MedicaidFormModal record={editing} clients={clients}
          onClose={()=>setShowModal(false)} onSaved={()=>{ setShowModal(false); load(); }}/>
      )}
      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header"><span className="modal-title">🗑️ Delete Authorization</span><button className="modal-close" onClick={()=>setDeleting(null)}>✕</button></div>
            <div className="modal-body"><p style={{color:'var(--gray-600)'}}>Delete authorization for <strong>{deleting.ClientName}</strong>?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>{delLoad?'Deleting…':'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MedicaidFormModal({ record, clients, onClose, onSaved }) {
  const isEdit = !!record;
  const now    = new Date();
  const [form, setForm] = useState({
    ClientID:            record?.ClientID            || '',
    AuthorizationNumber: record?.AuthorizationNumber || '',
    AuthMonth:           record?.AuthMonth           || now.getMonth()+1,
    AuthYear:            record?.AuthYear            || now.getFullYear(),
    AuthorizedHours:     record?.AuthorizedHours     || '',
    UsedHours:           record?.UsedHours           || '0',
    IsActive:            record?.IsActive !== undefined ? record.IsActive : 1,
    Notes:               record?.Notes               || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f,v) => setForm(p=>({...p,[f]:v}));

  const handleSave = async () => {
    if (!form.ClientID || !form.AuthorizedHours) { toast.error('Client and authorized hours are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await medicaidAPI.update(record.ID,form); toast.success('Authorization updated.'); }
      else        { await medicaidAPI.create(form);           toast.success('Authorization created.'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Save failed.'); }
    finally        { setSaving(false); }
  };

  const years = Array.from({length:5},(_,i)=>now.getFullYear()-2+i);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit?'✏️ Edit Authorization':'🏥 New Medicaid Authorization'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className="form-control" value={form.ClientID} onChange={e=>set('ClientID',e.target.value)}>
                <option value="">— Select —</option>
                {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Authorization Number</label>
              <input className="form-control" value={form.AuthorizationNumber} onChange={e=>set('AuthorizationNumber',e.target.value)} placeholder="Auth #"/>
            </div>
          </div>
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Month</label>
              <select className="form-control" value={form.AuthMonth} onChange={e=>set('AuthMonth',parseInt(e.target.value))}>
                {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Year</label>
              <select className="form-control" value={form.AuthYear} onChange={e=>set('AuthYear',parseInt(e.target.value))}>
                {years.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.IsActive} onChange={e=>set('IsActive',parseInt(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Authorized Hours <span className="req">*</span></label>
              <input className="form-control" type="number" step="0.5" value={form.AuthorizedHours} onChange={e=>set('AuthorizedHours',e.target.value)} placeholder="0"/>
            </div>
            <div className="form-group">
              <label className="form-label">Used Hours</label>
              <input className="form-control" type="number" step="0.5" value={form.UsedHours} onChange={e=>set('UsedHours',e.target.value)} placeholder="0"/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.Notes} onChange={e=>set('Notes',e.target.value)} placeholder="Optional notes…"/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'💾 Update':'✅ Add Authorization'}</button>
        </div>
      </div>
    </div>
  );
}